
import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './ProductPageHero.css';
import { TCreditCard } from '../../types/models/CreditCard';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { useWidthContext } from '../../providers/Width';
import { Row, Col, Title, Image, Section, Banner, Video, List } from '../../importCL';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';

enum Version {
    ProductPage = 'productPage',
    ProductFamilyPage = 'productFamilyPage'
}

interface IProductPageHeroProps extends MappedComponentProperties {
    title?: string;
    subTitle?: string;
    annualFeeFlagText?: string;
    version?: Version;
    backgroundImage?: string;
    mobileBackgroundImage?: string;
    videoTitle?: string;
    videoUrl?: string;
    ctaText?: string;
    ctaLink?: string;
    featuredCard?: TCreditCard;
    twoCardVariant?: boolean;
}

export const ProductPageHeroEditConfig = {
    emptyLabel: 'Product Page Hero',
    isEmpty: (props: PropsWithChildren<IProductPageHeroProps>) => {
        return !props || !props.title;
    },
};

const CardTitle = ({ children, md }) => {
    return (
        <Col xs={12} md={md}>
            <Title className='ProductPageHero__card-title' tag='h5'>
                {children}
            </Title>
        </Col>
    );
};

const APRInfo = ({ children }) => {
    return (
        <Col xs={12} md={8}>
            <p className='ProductPageHero__apr-info'>
                {children}
            </p>
        </Col>
    );
};

const ProductPageHero = (props: PropsWithChildren<IProductPageHeroProps>): JSX.Element => {
    const { width } = useWidthContext();
    const isMobile = width <= 768;
    const isFamilyPage = props.version === Version.ProductFamilyPage;
    const className = `Product${isFamilyPage ? 'Family' : 'Page'}Hero`;
    const videoUrl = !isFamilyPage && props.videoUrl ? props.videoUrl : '';
    const buttonVariant = isFamilyPage ? 'ghosted-light' : 'solid';

    const cardTitle = props.title ?? props.featuredCard?.productName ?? '';
    const cardSubTitle = props.subTitle ?? props.featuredCard?.productDescription ?? '';
    const pqCtaLink = props.ctaLink ?? props.featuredCard?.preQualifyLink ?? '';
    const ctaText = props.ctaText ?? 'See If You Pre-Qualify';
    const subTitleClassName = `${className}__subtitle${props.twoCardVariant ? ' twoCardVariant' : ''}`
    const titleClassName = props.twoCardVariant ? 'ProductPageHero__gridTitle' : isFamilyPage ? 'ProductFamilyHero__title' : 'ProductPageHero__title';
    const termsLink = props.featuredCard?.staticLink || props.featuredCard?.dynamicTermsLink;

    let bgImg = isFamilyPage ? props.backgroundImage : '';
    if (isFamilyPage && isMobile) {
        bgImg = props.mobileBackgroundImage;
    }

    const PreQualifyButton = () => {
        return (
            <ButtonWrapper
                size={props.twoCardVariant ? 'large' : 'medium'}
                href={pqCtaLink}
                variant={buttonVariant}
                className={`${className}__button`}>
                {transformHTMLParserLinks(ctaText)}
            </ButtonWrapper>
        );
    };

    return (
        <Section id='product-page-hero' className={className} bgImg={bgImg}>
            <Row className={props.twoCardVariant ? 'ProductPageHero__grid' : 'ProductPageHero__row'} data-testid='container' >
                <Col className={`ProductPageHero__col-one${videoUrl ? '-video' : ''} ${props.twoCardVariant && 'ProductPageHero__col-title'}`} data-testid='col' xs={props.twoCardVariant ? 12 : 6} sm={props.twoCardVariant ? 12 : isFamilyPage ? 4 : 6} md={props.twoCardVariant ? 12 : 6}>
                    {props.annualFeeFlagText &&
                        <div className='ProductPageHero__bannerContainer'>
                            <div className='ProductPageHero__bannerTitle'>
                                <Banner className='ProductPageHero__banner'>{transformHTMLParserLinks(props.annualFeeFlagText)}</Banner>
                                <Title className={titleClassName}
                                    spanTitle={transformHTMLParserLinks(cardSubTitle)}
                                    spanClass={subTitleClassName}
                                    verticalBar={props.twoCardVariant}
                                    data-testid='title'>
                                    {transformHTMLParserLinks(cardTitle)}
                                </Title>
                            </div>
                        </div>
                    }
                    {((isFamilyPage || videoUrl) && props.featuredCard?.imagePath) &&
                        <Image className='col-one-img'
                            loading='lazy'
                            alt={props.featuredCard?.cardImageAltText}
                            src={props.featuredCard.imagePath} />
                    }
                    {!props.annualFeeFlagText && <Title className={titleClassName} spanTitle={transformHTMLParserLinks(cardSubTitle)} spanClass={subTitleClassName} verticalBar={props.twoCardVariant} data-testid='title'
                    >{transformHTMLParserLinks(cardTitle)} </Title>}
                    {(width > 768 || isFamilyPage) && <PreQualifyButton />}
                </Col>
                {(width <= 768 && props.twoCardVariant) && (
                    <Col className='ProductPageHero__cta'>
                        <PreQualifyButton />
                    </Col>
                )}
                {(!isFamilyPage) &&
                    <Col xs={12} md={props.twoCardVariant ? 12 : 6} className='ProductPageHero__col-two'>
                        {videoUrl && <Video youtubeEmbedURL={videoUrl} title={props.videoTitle ?? 'Video'} />}
                        {(!videoUrl && props.featuredCard?.imagePath) &&
                            <Image className='ProductPageHero__col-two-img'
                                loading='lazy'
                                alt={props.featuredCard?.cardImageAltText}
                                src={props.featuredCard.imagePath} />
                        }
                    </Col>
                }
            </Row>
            {props.twoCardVariant && (
                <Row className='ProductPageHero__infoRow'>
                    <Col className='ProductPageHero__cards recommended' xs={12} md={6}>
                        <CardTitle md={5}>
                            Recommended for:
                        </CardTitle>
                        <Col className='ProductPageHero__recommended-container' xs={12} md={7}>
                            {props.featuredCard?.recommendedFeatures &&
                                <List className='ProductPageHero__recommended-list' items={props.featuredCard.recommendedFeatures} checks />
                            }
                        </Col>
                    </Col>
                    <Col className='ProductPageHero__cards' xs={12} md={6}>
                        <Row className='ProductPageHero__apr-row'>
                            <CardTitle md={4}>
                                {transformHTMLParserLinks(props.featuredCard?.aprTitle)}
                            </CardTitle>
                            <APRInfo>
                                <strong>{props.featuredCard?.apr}</strong>{" "}{props.featuredCard?.aprText}
                            </APRInfo>
                        </Row>
                        <Row className='ProductPageHero__apr-row'>
                            <CardTitle md={4}>
                                {transformHTMLParserLinks(props.featuredCard?.annualFeeTitle)}
                            </CardTitle>
                            <APRInfo>
                                {transformHTMLParserLinks(props.featuredCard?.annualFeeText)}
                            </APRInfo>
                        </Row>
                        {transformHTMLParserLinks(props.featuredCard?.aprDisclaimer)}{" "}
                        <LinkWrapper href={termsLink}>
                            {transformHTMLParserLinks(props.featuredCard?.aprDisclaimerText)}
                        </LinkWrapper>
                    </Col>
                </Row>
            )}
        </Section>
    );
};

export default ProductPageHero;